import type { ReactNode } from 'react';
import { SkeletonRow } from './Skeleton.js';
import { EmptyState } from './EmptyState.js';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  align?: 'left' | 'right';
}

interface Props<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  loading?: boolean;
}

export function DataTable<T>({ rows, columns, rowKey, onRowClick, emptyMessage, loading }: Props<T>) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={
                    'px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500 ' +
                    (c.align === 'right' ? 'text-right' : 'text-left') +
                    ' ' +
                    (c.className ?? '')
                  }
                  scope="col"
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              Array.from({ length: 5 }, (_, i) => <SkeletonRow key={i} cols={columns.length} />)
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-2 py-8">
                  <EmptyState title="Nothing here yet" description={emptyMessage} />
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={
                        'px-4 py-3 align-middle ' +
                        (c.align === 'right' ? 'text-right' : 'text-left') +
                        ' ' +
                        (c.className ?? '')
                      }
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
